import { useCallback } from 'react'
import BackofficePanel, { PanelError, Row } from '../BackofficePanel'
import { relativeTime } from '../feedText'
import { useAdminPoll } from '../../../app/backoffice/useAdminPoll'
import {
  getAdminFootballStatus,
  getDataCoverage,
  getProviderRequests,
} from '../../../api/admin'
import { backoffSeconds } from '../backoff'
import type { FootballStatusResponse, SyncResourceStatus } from '../../../footballStatus'

/**
 * The pipeline health panel.
 *
 * `GET /api/dev/football-status` is the first thing anyone looks at on a matchday. This is that,
 * hosted where a full screen affords what a rail could not: the provider request history and the
 * data-coverage check below.
 *
 * Read-only and available in all three modes, including `live` — that is the entire point.
 */

const STATUS_POLL_MS = 10_000
const SLOW_POLL_MS = 60_000

export default function PipelineHealthPanel({ enabled }: { enabled: boolean }) {
  const loadStatus = useCallback(() => getAdminFootballStatus(), [])
  const status = useAdminPoll(loadStatus, { enabled, intervalMs: STATUS_POLL_MS })

  const loadRequests = useCallback(() => getProviderRequests(7), [])
  const requests = useAdminPoll(loadRequests, { enabled, intervalMs: SLOW_POLL_MS })

  const loadCoverage = useCallback(() => getDataCoverage(), [])
  const coverage = useAdminPoll(loadCoverage, { enabled, intervalMs: SLOW_POLL_MS })

  const nowMs = status.receivedAtMs ?? 0

  return (
    <BackofficePanel
      title="Pipeline health"
      badge={status.data ? (status.data.paused ? 'paused' : 'ok') : '…'}
      subtitle={
        status.data
          ? `${status.data.driver} · ${status.data.competition} ${status.data.season} · ${status.data.baseUrl}`
          : undefined
      }
    >
      {status.error !== null && <PanelError error={status.error} />}
      {status.data && <StatusBlock status={status.data} nowMs={nowMs} />}

      {requests.error !== null && <PanelError error={requests.error} />}
      {requests.data && (
        <div className="bo-group">
          <p className="bo-group__title">Provider requests, last {requests.data.days} days</p>
          {/* Bars rather than a table: the question is "what is the shape of our spend", and a
              day that blew past the budget must be visible without reading a number. */}
          <div className="bo-bars">
            {requests.data.byDay.map((day) => {
              const ceiling = Math.max(requests.data!.dailyBudget, 1)
              const height = Math.min(100, Math.round((day.total / ceiling) * 100))
              return (
                <div
                  key={day.day}
                  className={`bo-bars__bar ${day.total > ceiling ? 'bo-bars__bar--over' : ''}`}
                  style={{ height: `${Math.max(2, height)}%` }}
                  title={`${day.day}: ${day.total} requests (${day.failed} failed), budget ${ceiling}`}
                />
              )
            })}
          </div>
          {/* The answer to "what is spending the budget" is almost always one endpoint. */}
          {requests.data.byEndpoint.slice(0, 6).map((entry) => (
            <Row
              key={entry.endpoint}
              label={entry.endpoint}
              value={`${entry.total}${entry.failed > 0 ? ` (${entry.failed} failed)` : ''}`}
            />
          ))}
          {requests.data.recentFailures.length > 0 && (
            <>
              <p className="bo-group__title">Recent failures</p>
              {requests.data.recentFailures.slice(0, 10).map((failure, index) => (
                <Row
                  key={`${failure.requestedAt}-${index}`}
                  label={failure.endpoint}
                  value={`${failure.errorKind ?? 'error'} · ${relativeTime(failure.requestedAt, nowMs)}`}
                />
              ))}
            </>
          )}
        </div>
      )}

      {coverage.error !== null && <PanelError error={coverage.error} />}
      {coverage.data && (
        <div className="bo-group">
          <p className="bo-group__title">Data coverage</p>
          {Object.entries(coverage.data.fixturesByStatus).map(([key, value]) => (
            <Row key={key} label={key} value={String(value)} />
          ))}
          {/* A real symptom, not a curiosity: `applyFixtures` skips a fixture it cannot place in a
              gameweek and only `console.warn`s, so a mapping bug shows up here and nowhere else. */}
          {coverage.data.gameweeksWithNoFixtures.length > 0 && (
            <p className="bo-error">
              gameweeks with no fixtures: {coverage.data.gameweeksWithNoFixtures.join(', ')}
            </p>
          )}
          {coverage.data.unmappedTeamRefs.length > 0 && (
            <p className="bo-error">unmapped team refs: {coverage.data.unmappedTeamRefs.join(', ')}</p>
          )}
          {coverage.data.unknownStatusFixtures.length > 0 && (
            <p className="bo-error">
              {coverage.data.unknownStatusFixtures.length} fixture(s) in unknown status —{' '}
              {coverage.data.unknownStatusFixtures.slice(0, 5).join(', ')}
            </p>
          )}
          <Row
            label="finished, no detail"
            value={String(coverage.data.finishedWithoutDetail.length)}
          />
        </div>
      )}
    </BackofficePanel>
  )
}

function StatusBlock({ status, nowMs }: { status: FootballStatusResponse; nowMs: number }) {
  const budgetPercent =
    status.dailyBudget > 0
      ? Math.min(100, Math.round((status.requestsToday / status.dailyBudget) * 100))
      : 0

  return (
    <>
      {!status.schedulerRunning && (
        <p className="bo-error">scheduler not running — nothing polls and nothing will self-heal</p>
      )}
      {!status.fixturesLoaded && (
        <p className="bo-error">no fixtures loaded — the cold sync has not run</p>
      )}
      {/* A paused scheduler looks identical to a healthy one from every other screen in the app. */}
      {status.paused === true && (
        <p className="bo-error">
          paused{status.pausedUntil ? ` until ${status.pausedUntil}` : ''}
          {status.pausedReason ? `: ${status.pausedReason}` : ''}
        </p>
      )}

      {/* These two disagreeing is itself the diagnosis, which is why both are shown rather than
          reconciled — and why the reason is rendered verbatim. */}
      {status.liveWindowOpen !== null && (
        <Row label="live window" value={status.liveWindowOpen ? 'open' : 'closed'} />
      )}
      {status.liveWindowReason !== null && <Row label="why" value={status.liveWindowReason} />}
      {status.anyFixtureInPlay !== null && (
        <Row label="in play" value={status.anyFixtureInPlay ? 'yes' : 'no'} />
      )}
      {status.phase && (
        <Row
          label={`GW${status.phase.gameweek} phase`}
          value={`${status.phase.deadlinePassed ? 'locked' : 'open'} · ${
            status.phase.resultsRevealed ? 'results in' : 'awaiting results'
          }`}
        />
      )}

      <Row
        label="requests today"
        value={`${status.requestsToday}/${status.dailyBudget}${
          status.budgetLevel ? ` (${status.budgetLevel})` : ''
        }`}
      />
      {/* A bar, not a number — the question is "are we about to run out". */}
      <div className="bo-meter">
        <div
          className={`bo-meter__fill ${
            status.budgetLevel === 'conserve'
              ? 'bo-meter__fill--conserve'
              : status.budgetLevel === 'exhausted'
                ? 'bo-meter__fill--exhausted'
                : ''
          }`}
          style={{ width: `${budgetPercent}%` }}
        />
      </div>
      {status.quotaRemaining !== null && (
        <Row label="provider quota" value={String(status.quotaRemaining)} />
      )}

      {Object.entries(status.resources).map(([resource, state]) => (
        <ResourceRow key={resource} resource={resource} state={state} nowMs={nowMs} />
      ))}
    </>
  )
}

function ResourceRow({
  resource,
  state,
  nowMs,
}: {
  resource: string
  state: SyncResourceStatus
  nowMs: number
}) {
  const failing = state.consecutiveFailures > 0 && state.lastError !== null
  return (
    <>
      <Row
        label={resource}
        value={state.lastSuccessAt ? relativeTime(state.lastSuccessAt, nowMs) : 'never'}
      />
      {failing && (
        <p className="bo-error">
          {state.lastError} (×{state.consecutiveFailures})
          {/* "Next attempt in 12m" is the question anyone staring at a failing resource is
              actually asking, and it is computable from what the payload already carries. */}
          {state.lastAttemptAt !== null &&
            ` — next attempt ${nextAttemptLabel(state, nowMs)}`}
        </p>
      )}
    </>
  )
}

function nextAttemptLabel(state: SyncResourceStatus, nowMs: number): string {
  if (state.lastAttemptAt === null) return 'unknown'
  const dueMs = Date.parse(state.lastAttemptAt) + backoffSeconds(state.consecutiveFailures) * 1000
  const seconds = Math.round((dueMs - nowMs) / 1000)
  if (seconds <= 0) return 'now'
  if (seconds < 60) return `in ${seconds}s`
  return `in ${Math.round(seconds / 60)}m`
}
